import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import Button from './Button';
import ContractCheckerSuccessView from '../contractChecker/ContractCheckerSuccessView';
import { saveContractTextForCarryover } from '../../lib/contractChecker/carryoverStorage.js';
import { stitchedFullDraftFromRewrites } from '../../lib/brandDealChecker/normalizeRewriteResponse';
import { PERSONALIZED_REWRITE_LIMIT_MESSAGE } from '../../lib/brandDealChecker/personalizedRateLimitCopy.js';

const DEFAULT_DOCUMENT_TITLE = 'InfluenceHub | The brand-ready media kit serious creators use';

const PAGE_TITLE = 'Influencer Contract Checker | Review Brand Deals & Avoid Bad Terms';
const PAGE_DESCRIPTION =
  'Paste your influencer contract or brand deal and instantly detect risky terms, unfair clauses, and hidden red flags. Built for creators.';
const PAGE_KEYWORDS = 'influencer contract checker, brand deal contract review, influencer contract template';

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is an influencer contract?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'An influencer contract is a written agreement between a creator and a brand. It usually covers deliverables, payment, timing, usage rights, and other rules for the partnership.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I know if a brand deal is fair?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Compare the terms to what you were promised verbally, check that pay, scope, and deadlines are clear, and look for one-sided language. When something feels vague or permanent, ask the brand to clarify before you sign.',
      },
    },
    {
      '@type': 'Question',
      name: 'What should I look for in a contract?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Focus on payment timing, what you must deliver, how long the brand can use your content, exclusivity limits, and how either side can end the deal. Our tool highlights common risk areas, but it is not legal advice.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I negotiate influencer contracts?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Many terms are negotiable, especially scope, usage length, exclusivity, and payment schedule. Ask for changes in writing so the final contract matches what you agreed.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this tool a substitute for a lawyer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. This checker offers general educational feedback to help you spot issues faster. For high-stakes deals or if you are unsure, consult a qualified attorney in your jurisdiction.',
      },
    },
  ],
};

const MAX_CHARS = 15000;

function trackEvent(name, params) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', name, params || {});
  }
}

function useInfluencerContractCheckerSeo() {
  useEffect(() => {
    const canonicalPath = '/influencer-contract-checker';
    const origin = window.location.origin;
    const pageUrl = `${origin}${canonicalPath}`;
    const ogImage = `${origin}/og.png`;

    const cleanups = [];

    const prevTitle = document.title;
    document.title = PAGE_TITLE;
    cleanups.push(() => {
      document.title = prevTitle;
    });

    const touchMeta = (attr, key, value) => {
      const el = document.querySelector(`meta[${attr}="${CSS.escape(key)}"]`);
      if (el) {
        const prev = el.getAttribute('content');
        el.setAttribute('content', value);
        cleanups.push(() => {
          if (prev === null || prev === undefined) el.removeAttribute('content');
          else el.setAttribute('content', prev);
        });
      } else {
        const m = document.createElement('meta');
        m.setAttribute(attr, key);
        m.setAttribute('content', value);
        m.setAttribute('data-ih-seo', 'contract-checker');
        document.head.appendChild(m);
        cleanups.push(() => m.remove());
      }
    };

    touchMeta('name', 'description', PAGE_DESCRIPTION);
    touchMeta('name', 'keywords', PAGE_KEYWORDS);
    touchMeta('property', 'og:title', PAGE_TITLE);
    touchMeta('property', 'og:description', PAGE_DESCRIPTION);
    touchMeta('property', 'og:url', pageUrl);
    touchMeta('property', 'og:type', 'website');
    touchMeta('property', 'og:image', ogImage);
    touchMeta('name', 'twitter:card', 'summary_large_image');
    touchMeta('name', 'twitter:title', PAGE_TITLE);
    touchMeta('name', 'twitter:description', PAGE_DESCRIPTION);
    touchMeta('name', 'twitter:image', ogImage);

    let link = document.querySelector('link[rel="canonical"]');
    if (link) {
      const prevHref = link.getAttribute('href');
      link.setAttribute('href', pageUrl);
      cleanups.push(() => {
        if (prevHref === null || prevHref === undefined) link.removeAttribute('href');
        else link.setAttribute('href', prevHref);
      });
    } else {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', pageUrl);
      link.setAttribute('data-ih-seo', 'contract-checker');
      document.head.appendChild(link);
      cleanups.push(() => link.remove());
    }

    return () => {
      cleanups.reverse().forEach((fn) => fn());
    };
  }, []);
}

function InfluencerContractCheckerSeoSections() {
  return (
    <div className="space-y-12 sm:space-y-14">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-dark font-dm-sans mb-6">
          Influencer Contract Checker: What to Look For Before You Sign
        </h2>
        <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-dm-sans mb-8 max-w-2xl">
          A quick read on common issues in creator agreements. Use it alongside the tool above, not as a replacement for professional advice.
        </p>

        <div className="space-y-10">
          <section aria-labelledby="seo-red-flags">
            <h3 id="seo-red-flags" className="text-lg font-semibold text-dark font-dm-sans mb-3">
              Common Influencer Contract Red Flags
            </h3>
            <ul className="list-disc list-inside text-sm sm:text-base text-gray-600 space-y-2 font-dm-sans leading-relaxed max-w-2xl">
              <li>Vague payment terms (net days, triggers, or fees unclear)</li>
              <li>Unlimited or perpetual usage rights beyond the campaign</li>
              <li>Broad exclusivity that blocks other brand work</li>
              <li>Unclear deliverables, formats, or approval steps</li>
              <li>Termination rules that let one side exit without clarity on pay or content use</li>
            </ul>
          </section>

          <section aria-labelledby="seo-how-review">
            <h3 id="seo-how-review" className="text-lg font-semibold text-dark font-dm-sans mb-3">
              How to Review a Brand Deal Contract
            </h3>
            <ul className="list-disc list-inside text-sm sm:text-base text-gray-600 space-y-2 font-dm-sans leading-relaxed max-w-2xl">
              <li>Scan payment terms: amount, currency, invoice timing, and late fees</li>
              <li>Check usage rights: paid ads, organic reposts, whitelisting, and territory</li>
              <li>Note exclusivity length, category scope, and carve-outs</li>
              <li>Clarify revision rounds, turnaround times, and who approves final assets</li>
            </ul>
          </section>

          <section aria-labelledby="seo-templates">
            <h3 id="seo-templates" className="text-lg font-semibold text-dark font-dm-sans mb-3">
              Do Influencers Need a Contract Template?
            </h3>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-dm-sans max-w-2xl">
              Templates can help you learn standard sections, but many brands send their own paper. What matters most is reading what you sign, asking questions, and aligning the written terms with your understanding of the deal.
            </p>
          </section>
        </div>
      </div>

      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-dark font-dm-sans mb-6">Frequently Asked Questions</h2>
        <dl className="space-y-6 max-w-2xl">
          <div>
            <dt className="text-sm sm:text-base font-semibold text-dark font-dm-sans">What is an influencer contract?</dt>
            <dd className="mt-1.5 text-sm sm:text-base text-gray-600 leading-relaxed font-dm-sans">
              It is an agreement between you and a brand that sets expectations for content, pay, timing, and rights. It should match what you discussed before you commit.
            </dd>
          </div>
          <div>
            <dt className="text-sm sm:text-base font-semibold text-dark font-dm-sans">How do I know if a brand deal is fair?</dt>
            <dd className="mt-1.5 text-sm sm:text-base text-gray-600 leading-relaxed font-dm-sans">
              Fairness depends on scope, pay, and risk. Clear language, reasonable usage windows, and balanced termination terms are good signs. If key numbers or dates are missing, pause and ask.
            </dd>
          </div>
          <div>
            <dt className="text-sm sm:text-base font-semibold text-dark font-dm-sans">What should I look for in a contract?</dt>
            <dd className="mt-1.5 text-sm sm:text-base text-gray-600 leading-relaxed font-dm-sans">
              Prioritize payment, deliverables, usage rights, exclusivity, and how the deal ends. Our checker surfaces these themes in plain language but does not provide legal advice.
            </dd>
          </div>
          <div>
            <dt className="text-sm sm:text-base font-semibold text-dark font-dm-sans">Can I negotiate influencer contracts?</dt>
            <dd className="mt-1.5 text-sm sm:text-base text-gray-600 leading-relaxed font-dm-sans">
              Yes. Brands often expect questions on usage length, exclusivity, and payment timing. Get material changes reflected in the final document, not only in email.
            </dd>
          </div>
          <div>
            <dt className="text-sm sm:text-base font-semibold text-dark font-dm-sans">Is this tool a substitute for a lawyer?</dt>
            <dd className="mt-1.5 text-sm sm:text-base text-gray-600 leading-relaxed font-dm-sans">
              No. Use it to educate yourself and prepare questions. For complex deals or legal uncertainty, work with a qualified attorney.
            </dd>
          </div>
        </dl>
      </div>

      <p className="text-sm text-gray-600 font-dm-sans leading-relaxed">
        <a href="/" className="text-primary font-medium hover:text-primary/80 underline-offset-2 hover:underline">
          Back to home
        </a>
        <span className="mx-2 text-gray-300" aria-hidden="true">
          ·
        </span>
        <a
          href="/create-influencer-profile"
          className="text-primary font-medium hover:text-primary/80 underline-offset-2 hover:underline"
        >
          Create a creator profile
        </a>
      </p>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSON_LD) }}
      />
    </div>
  );
}

export default function BrandDealCheckerPage() {
  useInfluencerContractCheckerSeo();
  const { user, profile, loading: authLoading } = useAuth();
  const creatorRole = profile ? profile.userType || profile.role : null;
  const isCreator = creatorRole === 'influencer' || creatorRole === 'creator';

  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [requestError, setRequestError] = useState('');
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [rewriteError, setRewriteError] = useState('');
  const [rewriteData, setRewriteData] = useState(null);

  const charCount = text.length;
  const overLimit = charCount > MAX_CHARS;

  const runAnalyze = useCallback(async () => {
    setRequestError('');
    setResult(null);
    setRewriteOpen(false);
    setRewriteLoading(false);
    setRewriteError('');
    setRewriteData(null);
    const trimmed = text.trim();
    if (!trimmed) {
      setRequestError('Please paste your agreement or deal text.');
      return;
    }
    if (trimmed.length > MAX_CHARS) {
      setResult({
        status: 'too_long',
        message:
          'This text is too long for V1. Please paste the most relevant sections or shorten the agreement (up to 15,000 characters).',
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/brand-deal-checker/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok && !data.status) {
        const errMsg =
          typeof data.message === 'string'
            ? data.message
            : typeof data.error === 'string'
              ? data.error
              : data.error && typeof data.error.message === 'string'
                ? data.error.message
                : null;
        setRequestError(errMsg || 'Something went wrong. Please try again.');
        return;
      }
      setResult(data);
      if (data.status === 'success') {
        trackEvent('brand_deal_checker_success', {
          event_category: 'growth',
          risk_level: data.risk_level,
          score: data.score,
        });
      }
    } catch {
      setRequestError('Network error. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [text]);

  const generateSaferRevision = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || !result || result.status !== 'success') return;
    setRewriteOpen(true);
    setRewriteError('');
    setRewriteLoading(true);
    const usePersonalized = Boolean(user && isCreator);
    try {
      const payload = {
        contractText: trimmed,
        red_flags: Array.isArray(result.red_flags) ? result.red_flags : [],
        analysisContext: {
          score: result.score,
          risk_level: result.risk_level,
          quick_verdict: result.quick_verdict,
        },
        ...(usePersonalized ? { mode: 'personalized' } : {}),
      };
      const headers = { 'Content-Type': 'application/json' };
      if (usePersonalized) {
        const token = await user.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      }
      const res = await fetch('/api/brand-deal-checker/rewrite', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      let data = await res.json().catch(() => ({}));
      if (data.status === 'rate_limited') {
        setRewriteData(null);
        setRewriteError(
          data.message ||
            (usePersonalized ? PERSONALIZED_REWRITE_LIMIT_MESSAGE : 'Daily limit reached. Try again tomorrow or create an account.'),
        );
        return;
      }
      if (data.status !== 'success' || !Array.isArray(data.rewrites)) {
        setRewriteData(null);
        setRewriteError(data.message || 'Could not generate revisions. Try again.');
        return;
      }
      if (usePersonalized) {
        const fullOk = typeof data.full_rewritten_contract === 'string' && data.full_rewritten_contract.trim().length > 0;
        if (!fullOk && data.rewrites.length > 0) {
          data = {
            ...data,
            full_rewritten_contract: stitchedFullDraftFromRewrites(data.rewrites),
          };
        } else if (!fullOk) {
          setRewriteData(null);
          setRewriteError('Could not load your personalized full draft. Try again.');
          return;
        }
      }
      setRewriteData(data);
      trackEvent('brand_deal_checker_rewrite_success', {
        event_category: 'growth',
        rewrite_count: data.rewrites.length,
        personalized: usePersonalized,
      });
    } catch {
      setRewriteData(null);
      setRewriteError('Network error. Check your connection and try again.');
    } finally {
      setRewriteLoading(false);
    }
  }, [text, result, user, isCreator]);

  const resetAnother = () => {
    setResult(null);
    setRequestError('');
    setText('');
    setRewriteOpen(false);
    setRewriteLoading(false);
    setRewriteError('');
    setRewriteData(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="section-padding bg-light min-h-[70vh]">
      <div className="container max-w-3xl px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-dark mb-3 font-dm-sans">Influencer Contract Checker</h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
            Paste the text of your influencer contract or brand deal. You will get a quick summary, risk highlights, and questions to ask
            before you sign. This is a fast sanity check, not a substitute for a lawyer.
          </p>
        </div>

        {!result && (
          <>
            <p className="text-sm text-gray-600 text-center mb-6 max-w-2xl mx-auto leading-relaxed">
              This tool provides general informational feedback only and is not legal advice.
            </p>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 mb-6">
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-3 leading-relaxed">
                Do not paste bank details, home addresses, or highly sensitive personal information.
              </p>
              <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                By submitting text, you confirm you have the right to review and share it.
              </p>
              <label htmlFor="deal-text" className="sr-only">
                Agreement text
              </label>
              <textarea
                id="deal-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={14}
                placeholder="Paste agreement or deal terms here..."
                className="w-full min-h-[280px] sm:min-h-[320px] px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-base leading-relaxed resize-y font-dm-sans"
                disabled={loading}
                autoComplete="off"
                spellCheck="true"
              />
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
                <span className={`text-sm ${overLimit ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                  {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters
                </span>
                <Button type="button" size="lg" className="w-full sm:w-auto min-h-[48px]" onClick={runAnalyze} disabled={loading || overLimit}>
                  {loading ? 'Analyzing…' : 'Check my deal'}
                </Button>
              </div>
            </div>
          </>
        )}

        {requestError && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 text-sm" role="alert">
            {requestError}
          </div>
        )}

        {result && (
          <div className="space-y-6">
            {result.status === 'too_long' && (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-950 text-sm leading-relaxed">{result.message}</div>
            )}

            {result.status === 'unsupported_language' && (
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-gray-800 text-sm leading-relaxed">
                {result.message || 'This V1 currently works best with English-language contracts.'}
              </div>
            )}

            {result.status === 'not_contract' && (
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-gray-800 text-sm leading-relaxed">{result.message}</div>
            )}

            {result.status === 'rate_limited' && (
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-950 text-sm leading-relaxed">{result.message}</div>
            )}

            {result.status === 'error' && (
              <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 text-sm leading-relaxed space-y-3">
                <p>{result.message || 'Something went wrong. Please try again.'}</p>
                {result.debug_detail ? (
                  <pre className="text-xs whitespace-pre-wrap break-words bg-white/80 border border-red-100 rounded-lg p-3 text-gray-800 font-mono">
                    {result.debug_detail}
                  </pre>
                ) : null}
              </div>
            )}

            {result.status === 'success' && (
              <ContractCheckerSuccessView
                result={result}
                rewriteOpen={rewriteOpen}
                rewriteLoading={rewriteLoading}
                rewriteError={rewriteError}
                rewriteData={rewriteData}
                generateSaferRevision={generateSaferRevision}
                variant="public"
                rewriteExperience={!authLoading && user && isCreator ? 'creator' : 'public'}
                rewriteSignupHref="/signup"
              />
            )}

            <p className="text-sm text-gray-600 text-center leading-relaxed px-2">
              Consider consulting a qualified attorney before signing any agreement.
            </p>

            {result.status === 'success' && !authLoading && !user && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm text-left">
                <h2 className="text-lg font-semibold text-dark font-dm-sans mb-2">Get a personalized contract analysis</h2>
                <p className="text-sm text-gray-600 leading-relaxed mb-5 font-dm-sans">
                  Sign in to analyze this contract using your creator profile so the feedback is more tailored to your platform,
                  audience, and brand positioning.
                </p>
                <Button
                  type="button"
                  size="lg"
                  className="w-full sm:w-auto min-h-[48px]"
                  onClick={() => {
                    saveContractTextForCarryover(text);
                    window.location.assign('/login?next=' + encodeURIComponent('/profile/contract-tools'));
                  }}
                >
                  Sign in for personalized analysis
                </Button>
              </div>
            )}

            {result.status === 'success' && !authLoading && user && isCreator && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8 text-center">
                <Button
                  type="button"
                  size="lg"
                  className="w-full sm:w-auto min-h-[48px]"
                  onClick={() => {
                    saveContractTextForCarryover(text);
                    window.location.assign('/profile/contract-tools');
                  }}
                >
                  Analyze with my profile
                </Button>
              </div>
            )}

            <div className="flex justify-center pb-8">
              <Button type="button" variant="ghost" onClick={resetAnother}>
                Check another deal
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
